import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";
import { STRATEGIES, STRATEGY_BY_SLUG } from "@/data/strategies";

/**
 * /strategies/:slug — individual mandate detail page.
 */
const StrategyDetailPage = () => {
  const { slug } = useParams();
  const strategy = STRATEGY_BY_SLUG[slug];
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!strategy) {
    return <Navigate to="/strategies" replace />;
  }

  const idx = STRATEGIES.findIndex((s) => s.slug === slug);
  const next = STRATEGIES[(idx + 1) % STRATEGIES.length];

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.strategyDetailPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index={`Mandate ${strategy.n}`}
          label="Strategy · Investment Approach"
          title={
            <>
              {strategy.title.split(" ")[0]}{" "}
              <span className="italic text-[var(--kr-gold)]">
                {strategy.title.split(" ").slice(1).join(" ")}
              </span>
              .
            </>
          }
          description={strategy.summary}
          breadcrumb={`Strategies / ${strategy.title}`}
        />

        {/* Thesis */}
        <section className="py-20 lg:py-28">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Thesis
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.15]">
                Underwriting{" "}
                <span className="italic text-[var(--kr-gold)]">spine</span>.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <p className="text-[var(--kr-text-muted)] text-[17px] leading-[1.9] max-w-[62ch] border-l-2 border-[var(--kr-gold)] pl-8 italic font-serif-display">
                {strategy.thesis}
              </p>
            </div>
          </div>
        </section>

        {/* Approach + Sectors */}
        <section className="py-16 lg:py-24 border-t border-[var(--kr-line-soft)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-6">
                Investment Approach
              </div>
              <ul className="space-y-5">
                {strategy.approach.map((d) => (
                  <li
                    key={d}
                    className="flex items-start gap-4 text-[var(--kr-text)] text-[15px] leading-[1.75]"
                  >
                    <span
                      aria-hidden
                      className="mt-[8px] block w-[6px] h-[6px] flex-shrink-0"
                      style={{
                        background: "var(--kr-gold)",
                        transform: "rotate(45deg)",
                      }}
                    />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-5">
              <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-6">
                Target Sectors
              </div>
              <ul className="space-y-3">
                {strategy.sectors.map((s) => (
                  <li
                    key={s}
                    className="font-serif-display text-[1.05rem] text-[var(--kr-text)] border-b border-[var(--kr-line)] pb-3"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Deal parameters */}
        <section className="py-16 lg:py-24 border-t border-[var(--kr-line)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-12">
              <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                Deal Parameters
              </span>
              <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
              <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                Indicative · subject to documentation
              </span>
            </div>
            <dl className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
              {[
                ["Cheque Size", strategy.deal.size],
                ["Tenor", strategy.deal.tenor],
                ["Seniority", strategy.deal.seniority],
                ["Coverage", strategy.deal.coverage],
              ].map(([k, val]) => (
                <div
                  key={k}
                  className="p-8 lg:p-10 border-r border-b border-[var(--kr-line)]"
                >
                  <dt className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-3">
                    {k}
                  </dt>
                  <dd className="font-serif-display text-[1.3rem] text-[var(--kr-text)] leading-[1.3]">
                    {val}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Vehicles */}
        <section className="py-16 lg:py-24 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 mb-12">
              <div className="lg:col-span-6">
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                    Intended Vehicles
                  </span>
                  <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                </div>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.6rem,2.8vw,2.2rem)] leading-[1.2] max-w-[24ch]">
                  Capital deployed through{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    documented
                  </span>{" "}
                  vehicle architecture.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pl-8">
                <ul className="space-y-3">
                  {strategy.vehicles.map((v) => (
                    <li
                      key={v}
                      className="font-serif-display text-[1.15rem] text-[var(--kr-text)] border-b border-[var(--kr-line)] pb-3 flex items-center justify-between"
                    >
                      <span>{v}</span>
                      <span className="font-mono-label text-[9px] text-[var(--kr-text-dim)]">
                        · Working title
                      </span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/funds"
                  className="kr-link font-mono-label text-[11px] mt-8 inline-block"
                >
                  View full fund architecture →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA + Next mandate */}
        <section className="border-t border-[var(--kr-line)] py-20">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <h3 className="font-serif-display text-[var(--kr-text)] text-[1.6rem] leading-[1.2] max-w-[36ch]">
                Detailed mandate documentation is reserved for{" "}
                <span className="italic text-[var(--kr-gold)]">
                  qualified institutional
                </span>{" "}
                counterparties.
              </h3>
            </div>
            <div className="lg:col-span-6 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.strategyDetailRequest}
                className="kr-btn-primary"
              >
                Request Investor Access
                <span aria-hidden>→</span>
              </button>
              <Link
                to={`/strategies/${next.slug}`}
                data-testid={LANDING.strategyDetailNext}
                className="kr-btn-ghost"
              >
                Next · {next.title.split(" ")[0]} →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default StrategyDetailPage;
